import { Box, CircularProgress } from "@mui/material";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import React, { useEffect, useRef, useState } from "react";

const PULL_THRESHOLD = 70; // px of drag needed to trigger a refresh
const MAX_PULL = 100; // visual cap so the indicator doesn't chase the finger forever

// Live order-status updates ride a WebSocket subscription — if that
// connection drops (backgrounded tab, network hiccup, PWA suspend) there's
// otherwise no way back to fresh data short of reloading the whole app.
// This gives a native-feeling escape hatch: drag down from the very top of
// the page to force a refetch. The indicator is a fixed-position overlay
// rather than something that pushes content down, since the app header is
// itself position:fixed and wouldn't respond to a sibling's height change.
export default function PullToRefresh({ onRefresh, children }) {
  const wrapperRef = useRef(null);
  const startYRef = useRef(0);
  const pullingRef = useRef(false);
  const refreshingRef = useRef(false);
  const [pullDistance, setPullDistance] = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    refreshingRef.current = refreshing;
  }, [refreshing]);

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;

    const onTouchStart = (e) => {
      if (refreshingRef.current || window.scrollY > 0) return;
      startYRef.current = e.touches[0].clientY;
      pullingRef.current = true;
    };

    const onTouchMove = (e) => {
      if (!pullingRef.current || refreshingRef.current) return;
      const delta = e.touches[0].clientY - startYRef.current;
      if (delta <= 0) {
        pullingRef.current = false;
        setPullDistance(0);
        return;
      }
      // Suppress the native overscroll bounce so only our indicator animates.
      e.preventDefault();
      setPullDistance(Math.min(delta * 0.5, MAX_PULL));
    };

    const onTouchEnd = () => {
      if (!pullingRef.current) return;
      pullingRef.current = false;
      setPullDistance((current) => {
        if (current < PULL_THRESHOLD) return 0;
        setRefreshing(true);
        Promise.resolve()
          .then(onRefresh)
          .catch(() => {})
          .finally(() => {
            setRefreshing(false);
            setPullDistance(0);
          });
        return PULL_THRESHOLD;
      });
    };

    el.addEventListener("touchstart", onTouchStart, { passive: true });
    el.addEventListener("touchmove", onTouchMove, { passive: false });
    el.addEventListener("touchend", onTouchEnd, { passive: true });
    return () => {
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove", onTouchMove);
      el.removeEventListener("touchend", onTouchEnd);
    };
  }, [onRefresh]);

  const progress = Math.min(pullDistance / PULL_THRESHOLD, 1);
  const active = pullDistance > 0 || refreshing;

  return (
    <Box ref={wrapperRef}>
      <Box
        aria-hidden
        sx={{
          position: "fixed",
          top: 10,
          left: "50%",
          transform: `translate(-50%, ${active ? 0 : -60}px)`,
          opacity: active ? 1 : 0,
          transition: pullingRef.current ? "none" : "transform 0.25s ease, opacity 0.25s ease",
          zIndex: (t) => t.zIndex.modal + 200,
          width: 40,
          height: 40,
          borderRadius: "50%",
          bgcolor: "background.paper",
          boxShadow: "0 2px 10px rgba(0,0,0,0.18)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          pointerEvents: "none",
        }}
      >
        {refreshing ? (
          <CircularProgress size={20} color="primary" />
        ) : (
          <RefreshRoundedIcon
            sx={{ fontSize: 20, color: "primary.main", transform: `rotate(${progress * 180}deg)` }}
          />
        )}
      </Box>
      {children}
    </Box>
  );
}
